import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Button } from "react-bootstrap";
import { FaPhone, FaEnvelope, FaEdit, FaTrash } from "react-icons/fa";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
} from "@syncfusion/ej2-react-grids";

import Spinner from "../../Components/Spinner";
import DeleteModal from "../../Components/Modals/Delete-modal";
import SearchInputDocument from "../../Components/Forms/SearchInputDocument";
import EmptyData from "../../../components/EmptyData";
import { ToastComponent } from "../../Components/ToastComponent";
import { contactsService } from "../../../Services/contacts.service";
import { formatPhoneNumber } from "../../../Services/string.service";
import { paginationService } from "../../../Services/pagination.service";

const emptyForm = { contactId: null, name: "", doc: "", relationshipId: "", phone: "", email: "" };

// Editar/excluir o contato em si (nome/doc/vínculo) usa updateContact/
// deleteContact do contacts.service.js. Quando formData.contactId está
// preenchido, o form vira modo edição (telefone/e-mail ficam desabilitados,
// pois são geridos nos modais próprios).
export default function ContactsList({ clientId }) {
  const { t } = useTranslation();

  const [contacts, setContacts] = useState([]);
  const [phoneTypes, setPhoneTypes] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [contactsError, setContactsError] = useState(false);

  const [formData, setFormData] = useState(emptyForm);

  const [contactPhones, setContactPhones] = useState({});
  const [contactEmails, setContactEmails] = useState({});

  const [phoneModalContact, setPhoneModalContact] = useState(null);
  const [phoneNumberInput, setPhoneNumberInput] = useState("");
  const [phoneTypeInput, setPhoneTypeInput] = useState("");
  const [isLoadingPhoneModal, setIsLoadingPhoneModal] = useState(false);

  const [emailModalContact, setEmailModalContact] = useState(null);
  const [emailInput, setEmailInput] = useState("");
  const [isLoadingEmailModal, setIsLoadingEmailModal] = useState(false);

  const [toastParams, setToastParams] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Altura e paginação dinâmicos do grid (mesmo padrão de CustomerSearch.jsx)
  const [pageSize, setPageSize] = useState(15);
  const gridAnchorRef = useRef(null);

  const ROW_HEIGHT = 36;
  // gridAnchorRef já mede o "top" real do grid, então esse budget cobre só o
  // que fica ABAIXO dele — cabeçalho + pager do próprio grid Syncfusion.
  const HEADER_PAGER_BUDGET = 70;
  const BOTTOM_MARGIN = 134;
  const MIN_PAGE = 5;
  const MAX_PAGE = 100;

  const showToast = (title, body, color) => {
    setToastParams(JSON.stringify({ show: true, title, body, color }));
  };

  const calculateGridMetrics = () => {
    paginationService.calculateGridMetrics(
      gridAnchorRef,
      BOTTOM_MARGIN,
      HEADER_PAGER_BUDGET,
      ROW_HEIGHT,
      MIN_PAGE,
      MAX_PAGE,
      contacts,
      window.innerHeight,
      setPageSize
    );
  };

  useEffect(() => {
    if (contacts.length === 0) return undefined;

    calculateGridMetrics();

    let resizeTimeout = null;
    const onResize = () => {
      if (resizeTimeout) window.clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(calculateGridMetrics, 50);
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (resizeTimeout) window.clearTimeout(resizeTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts]);

  const asList = (response) =>
    Array.isArray(response) ? response : Array.isArray(response?.items) ? response.items : [];

  useEffect(() => {
    let isMounted = true;

    const loadContacts = async () => {
      setIsLoadingContacts(true);
      setContactsError(false);

      try {
        const [contactsResponse, phoneTypesResponse, relationshipsResponse] = await Promise.all([
          contactsService.fetchByClient(clientId),
          contactsService.fetchPhoneTypes(),
          contactsService.fetchRelationships(),
        ]);

        if (!isMounted) return;

        if (contactsResponse === null) {
          setContactsError(true);
          setContacts([]);
        } else {
          // GET /contacts/clients/{clientId} — o id do contato às vezes vem
          // como contactId/ContactId, às vezes só como id/Id (visto em
          // payload real onde contactId chegava undefined e sumia do JSON
          // enviado pro POST de telefone/e-mail). Cobre os dois formatos.
          const normalizedContacts = asList(contactsResponse).map((c) => ({
            contactId: c.contactId ?? c.ContactId ?? c.id ?? c.Id,
            name: c.name ?? c.Name,
            doc: c.doc ?? c.Doc,
            relationshipId: c.relationshipId ?? c.RelationshipId,
          }));
          setContacts(normalizedContacts);
        }
        setPhoneTypes(asList(phoneTypesResponse));
        setRelationships(asList(relationshipsResponse));
      } catch (error) {
        if (isMounted) {
          setContactsError(true);
          setContacts([]);
        }
      } finally {
        if (isMounted) setIsLoadingContacts(false);
      }
    };

    loadContacts();
    return () => {
      isMounted = false;
    };
  }, [clientId]);

  const relationshipName = (relationshipId) => {
    const found = relationships.find(
      (relationship) => (relationship.id ?? relationship.Id) === relationshipId
    );
    return found?.description ?? found?.Description ?? t("CUSTOMER.CONTACTS_LIST.RELATIONSHIP_UNKNOWN");
  };

  const phoneTypeName = (phoneTypeId) => {
    const found = phoneTypes.find(
      (phoneType) => (phoneType.id ?? phoneType.phoneTypeId ?? phoneType.PhoneTypeId) === phoneTypeId
    );
    return found?.description ?? found?.name ?? found?.Name ?? "";
  };

 const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearForm = () => setFormData(emptyForm);

  const handleEditContact = (contact) => {
    setFormData({
      contactId: contact.contactId,
      name: contact.name ?? "",
      doc: contact.doc ?? "",
      relationshipId: contact.relationshipId ?? "",
      phone: "",
      email: "",
    });
  };

  const [contactToDelete, setContactToDelete] = useState(null);

  const handleDeleteContact = (contact) => {
    setContactToDelete(contact);
  };

  const confirmDeleteContact = async (contactId) => {
    const response = await contactsService.deleteContact(clientId, contactId);


    if (response?.error) {
      showToast(
        t("COMMON.ERROR"),
        response?.friendlyMessage ?? t("CUSTOMER.CONTACTS_LIST.SAVE_CONTACT_ERROR"),
        "Danger"
      );
      return;
    }

    setContacts((prev) => prev.filter((item) => item.contactId !== contactId));
    if (formData.contactId === contactId) {
      setFormData(emptyForm);
    }
    showToast(t("COMMON.SUCCESS"), t("CUSTOMER.CONTACTS_LIST.CONTACT_DELETED"), "Success");
  };

  const handleSaveContact = async (e) => {
    e?.preventDefault?.();

    if (!formData.name.trim()) {
      showToast(t("COMMON.ATTENTION"), t("CUSTOMER.CONTACTS_LIST.NAME_REQUIRED"), "Warning");
      return;
    }

    if (!formData.relationshipId) {
      showToast(t("COMMON.ATTENTION"), t("CUSTOMER.CONTACTS_LIST.RELATIONSHIP_REQUIRED"), "Warning");
      return;
    }

    const isEditing = Boolean(formData.contactId);

    if (!isEditing && !formData.phone.trim()) {
      showToast(t("COMMON.ATTENTION"), t("CUSTOMER.CONTACTS_LIST.PHONE_REQUIRED"), "Warning");
      return;
    }

    if (!isEditing && !formData.email.trim()) {
      showToast(t("COMMON.ATTENTION"), t("CUSTOMER.CONTACTS_LIST.EMAIL_REQUIRED"), "Warning");
      return;
    }

    setIsSaving(true);
    try {
      const contactResponse = isEditing
        ? await contactsService.updateContact(formData.contactId, {
            name: formData.name.trim(),
            doc: formData.doc.trim(),
            relationshipId: formData.relationshipId,
          })
        : await contactsService.createContact({
            name: formData.name.trim(),
            doc: formData.doc.trim(),
            relationshipId: formData.relationshipId,
          });

      if (!contactResponse || contactResponse.error) {
        showToast(
          t("COMMON.ERROR"),
          contactResponse?.friendlyMessage ?? t("CUSTOMER.CONTACTS_LIST.SAVE_CONTACT_ERROR"),
          "Danger"
        );
        return;
      }

      if (isEditing) {
        setContacts((prev) =>
          prev.map((contact) =>
            contact.contactId === formData.contactId
              ? { ...contact, name: formData.name, doc: formData.doc, relationshipId: formData.relationshipId }
              : contact
          )
        );

        showToast(t("COMMON.SUCCESS"), t("CUSTOMER.CONTACTS_LIST.CONTACT_UPDATED"), "Success");
        setFormData(emptyForm);
        return;
      }

      const newContactId =
    typeof contactResponse === "number"
    ? contactResponse
    : contactResponse.contactId ??
      contactResponse.ContactId ??
      contactResponse.id ??
      contactResponse.Id ??
      contactResponse.data?.contactId ??
      contactResponse.data?.ContactId;

      setContacts((prev) => [
        ...prev,
        { contactId: newContactId, name: formData.name, doc: formData.doc, relationshipId: formData.relationshipId },
      ]);

      if (formData.phone.trim()) {
        const defaultPhoneType = phoneTypes[0]?.id ?? phoneTypes[0]?.phoneTypeId ?? phoneTypes[0]?.PhoneTypeId ?? "";
        const phoneResponse = await contactsService.createPhone({
          contactId: newContactId,
          phone: formData.phone.replace(/\D/g, ""),
          extension: "",
          phoneTypeId: defaultPhoneType,
        });
        if (phoneResponse && !phoneResponse.error) {
          setContactPhones((prev) => ({
            ...prev,
            [newContactId]: [
              {
                id: phoneResponse.contactPhoneId ?? phoneResponse.ContactPhoneId,
                number: formData.phone.replace(/\D/g, ""),
                extension: "",
                phoneTypeId: defaultPhoneType,
              },
            ],
          }));
        }
      }

      if (formData.email.trim()) {
        const emailResponse = await contactsService.createEmail({
          contactId: newContactId,
          email: formData.email.trim(),
        });
        if (emailResponse && !emailResponse.error) {
          setContactEmails((prev) => ({
            ...prev,
            [newContactId]: [
              { id: emailResponse.contactEmailId ?? emailResponse.ContactEmailId, email: formData.email.trim() },
            ],
          }));
        }
      }

      showToast(t("COMMON.SUCCESS"), t("CUSTOMER.CONTACTS_LIST.CONTACT_CREATED"), "Success");
      setFormData(emptyForm);
    } finally {
      setIsSaving(false);
    }
  };

  // Sempre busca do backend de novo — tanto ao abrir o modal quanto depois
  // de criar um telefone novo — em vez de confiar só no estado local
  // acumulado, que podia ficar desatualizado em relação ao que foi
  // realmente salvo.
  const refreshPhones = async (contactId) => {
    setIsLoadingPhoneModal(true);
    try {
      const response = await contactsService.fetchPhones(contactId);
      const normalized = asList(response).map((p) => ({
        id: p.id ?? p.Id,
        number: p.number ?? p.Number,
        extension: p.extension ?? p.Extension,
        phoneTypeId: p.phoneTypeId ?? p.PhoneTypeId,
      }));
      setContactPhones((prev) => ({ ...prev, [contactId]: normalized }));
    } catch (error) {
      setContactPhones((prev) => ({ ...prev, [contactId]: [] }));
    } finally {
      setIsLoadingPhoneModal(false);
    }
  };

  const openPhoneModal = (contact) => {
    setPhoneModalContact(contact);
    setPhoneNumberInput("");
    setPhoneTypeInput("");
    refreshPhones(contact.contactId);
  };

  const closePhoneModal = () => setPhoneModalContact(null);

  const handleAddPhone = async () => {
    if (!phoneNumberInput.trim()) return;

    const contactId = phoneModalContact.contactId;
    const response = await contactsService.createPhone({
      contactId,
      phone: phoneNumberInput.replace(/\D/g, ""),
      extension: "",
      phoneTypeId: phoneTypeInput,
    });

    if (!response || response.error) {
      showToast(t("COMMON.ERROR"), response?.friendlyMessage ?? t("CUSTOMER.CONTACTS_LIST.SAVE_PHONE_ERROR"), "Danger");
      return;
    }

    setPhoneNumberInput("");
    setPhoneTypeInput("");
    await refreshPhones(contactId);
  };

  const handleDeletePhone = async (phoneId) => {
    const contactId = phoneModalContact.contactId;
    const response = await contactsService.deletePhone(phoneId);

    if (!response || response.error) {
      showToast(t("COMMON.ERROR"), response?.friendlyMessage ?? t("CUSTOMER.CONTACTS_LIST.DELETE_PHONE_ERROR"), "Danger");
      return;
    }

    await refreshPhones(contactId);
  };

  // Mesmo motivo do refreshPhones: busca do backend de novo ao abrir o
  // modal e depois de criar um e-mail novo, em vez de confiar no estado
  // local acumulado.
  const refreshEmails = async (contactId) => {
    setIsLoadingEmailModal(true);
    try {
      const response = await contactsService.fetchEmails(contactId);
      const normalized = asList(response).map((e) => ({
        id: e.id ?? e.Id,
        email: e.email ?? e.Email,
      }));
      setContactEmails((prev) => ({ ...prev, [contactId]: normalized }));
    } catch (error) {
      setContactEmails((prev) => ({ ...prev, [contactId]: [] }));
    } finally {
      setIsLoadingEmailModal(false);
    }
  };

  const openEmailModal = (contact) => {
    setEmailModalContact(contact);
    setEmailInput("");
    refreshEmails(contact.contactId);
  };

  const closeEmailModal = () => setEmailModalContact(null);

  const handleAddEmail = async () => {
    if (!emailInput.trim()) return;

    const contactId = emailModalContact.contactId;
    const response = await contactsService.createEmail({ contactId, email: emailInput.trim() });

    if (!response || response.error) {
      showToast(t("COMMON.ERROR"), response?.friendlyMessage ?? t("CUSTOMER.CONTACTS_LIST.SAVE_EMAIL_ERROR"), "Danger");
      return;
    }

    setEmailInput("");
    await refreshEmails(contactId);
  };

  const handleDeleteEmail = async (emailId) => {
    const contactId = emailModalContact.contactId;
    const response = await contactsService.deleteEmail(emailId);

    if (!response || response.error) {
      showToast(t("COMMON.ERROR"), response?.friendlyMessage ?? t("CUSTOMER.CONTACTS_LIST.DELETE_EMAIL_ERROR"), "Danger");
      return;
    }

    await refreshEmails(contactId);
  };

  const currentPhones = contactPhones[phoneModalContact?.contactId] ?? [];
  const currentEmails = contactEmails[emailModalContact?.contactId] ?? [];

  return (
    <div className="p-3">
      <ToastComponent
        params={toastParams}
        closeToast={() => setToastParams(JSON.stringify({ show: false }))}
      />

      <div className="bg-primary p-2 rounded mb-3">
        <strong style={{ color: "var(--text-primary)" }}>{t("CUSTOMER.CONTACTS_LIST.PAGE_TITLE")}</strong>
      </div>

      <p className="text-muted small">{t("CUSTOMER.CONTACTS_LIST.TRUST_HINT")}</p>

      <form onSubmit={handleSaveContact} className="mb-4">
       <div className="row g-2 mb-2">
          <div className="col-md-4">
            <div className="input-group">
              <span className="input-group-text text-primary">{t("COMMON.NAME")}</span>
              <input
                type="text"
                className="form-control"
                value={formData.name}
                onChange={(e) => handleFormChange("name", e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-4">
            <SearchInputDocument
              name="contactDoc"
              label={t("CUSTOMER.CONTACTS_LIST.DOC_LABEL")}
              value={formData.doc}
              onChange={(e) => handleFormChange("doc", e.target.value)}
            />
          </div>
          <div className="col-md-4">
            <div className="input-group">
              <span className="input-group-text text-primary">{t("COMMON.TYPE")}</span>
              <select
                className="form-select"
                value={formData.relationshipId}
                onChange={(e) => handleFormChange("relationshipId", e.target.value)}
              >
                <option value="">{t("CUSTOMER.CONTACTS_LIST.SELECT_PLACEHOLDER")}</option>
                {relationships.map((relationship) => {
                  const id = relationship.id ?? relationship.Id;
                  const name = relationship.description ?? relationship.Description;
                  return (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </div>

        <div className="row g-2 mb-3">
          <div className="col-md-6">
            <div className="input-group">
              <span className="input-group-text text-primary">{t("CUSTOMER.CONTACTS_LIST.PHONE_LABEL")}</span>
              <input
                type="text"
                className="form-control"
                value={formatPhoneNumber(formData.phone)}
                onChange={(e) => handleFormChange("phone", e.target.value)}
                disabled={Boolean(formData.contactId)}
                maxLength={15}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="input-group">
              <span className="input-group-text text-primary">{t("CUSTOMER.CONTACTS_LIST.EMAIL_LABEL")}</span>
              <input
                type="text"
                className="form-control"
                value={formData.email}
                onChange={(e) => handleFormChange("email", e.target.value)}
                disabled={Boolean(formData.contactId)}
              />
            </div>
          </div>
        </div>

       <button type="submit" className="btn btn-primary me-2" disabled={isSaving}>
          {t("COMMON.SAVE")}
        </button>
        <button type="button" className="btn btn-outline-primary" onClick={handleClearForm}>
          {formData.contactId ? t("BUTTONS.CANCEL") : t("CUSTOMER.CONTACTS_LIST.CLEAR")}
        </button>
      </form>

      <div className="bg-dark text-white px-3 py-2">
        <strong>{t("CUSTOMER.CONTACTS_LIST.REGISTERED_TITLE")}</strong>
      </div>

      {isLoadingContacts ? (
        <div className="p-3">
          <Spinner />
        </div>
      ) : contactsError ? (
        <p className="text-danger mt-3">{t("CUSTOMER.CONTACTS_LIST.LOAD_CONTACTS_ERROR")}</p>
      ) : contacts.length === 0 ? (
        <div className="mt-3">
          <EmptyData />
        </div>
      ) : (
        <>
          <div ref={gridAnchorRef} />
          <GridComponent
            style={{ marginTop: 16 }}
            dataSource={contacts.map((contact) => ({
              ...contact,
              relationshipDisplay: relationshipName(contact.relationshipId),
            }))}
            allowPaging
            pageSettings={{ pageSize }}
            rowHeight={ROW_HEIGHT}
            height="auto"
            actionComplete={calculateGridMetrics}
          >
            <ColumnsDirective>
              <ColumnDirective field="name" headerText={t("COMMON.NAME")} width="200" />
              <ColumnDirective
                field="relationshipDisplay"
                headerText={t("COMMON.TYPE")}
                width="160"
              />
              <ColumnDirective
                headerText={t("CUSTOMER.CONTACTS_LIST.COLUMN_ACTIONS")}
                width="100"
                textAlign="Center"
                template={(row) => (
                  <div className="py-1">
                    <button
                      type="button"
                      className="btn btn-link text-primary p-1 me-1"
                      onClick={() => openPhoneModal(row)}
                      title={t("CUSTOMER.CONTACTS_LIST.PHONES_TITLE")}
                    >
                      <FaPhone />
                    </button>
                    <button
                      type="button"
                      className="btn btn-link text-primary p-1 me-1"
                      onClick={() => openEmailModal(row)}
                      title={t("CUSTOMER.CONTACTS_LIST.EMAILS_TITLE")}
                    >
                      <FaEnvelope />
                    </button>
                    <button
                      type="button"
                      className="btn btn-link text-primary p-1 me-1"
                      onClick={() => handleEditContact(row)}
                      title={t("CUSTOMER.CONTACTS_LIST.EDIT")}
                    >
                      <FaEdit />
                    </button>
                    <button
                      type="button"
                      className="btn btn-link text-danger p-1"
                      onClick={() => handleDeleteContact(row)}
                      title={t("CUSTOMER.CONTACTS_LIST.DELETE")}
                    >
                      <FaTrash />
                    </button>
                  </div>
                )}
              />
            </ColumnsDirective>
            <Inject services={[Page]} />
          </GridComponent>
        </>
      )}

      <Modal show={Boolean(phoneModalContact)} onHide={closePhoneModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("CUSTOMER.CONTACTS_LIST.ADD_PHONES_TITLE")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-2 align-items-end mb-3">
            <div className="col-6">
              <label className="form-label">{t("CUSTOMER.CONTACTS_LIST.PHONE_LABEL")}</label>
              <input
                type="text"
                className="form-control"
                value={formatPhoneNumber(phoneNumberInput)}
                onChange={(e) => setPhoneNumberInput(e.target.value)}
                maxLength={15}
              />
            </div>
            <div className="col-4">
              <label className="form-label">{t("COMMON.TYPE")}</label>
              <select
                className="form-select"
                value={phoneTypeInput}
                onChange={(e) => setPhoneTypeInput(e.target.value)}
              >
                <option value="">{t("CUSTOMER.CONTACTS_LIST.SELECT_PLACEHOLDER")}</option>
                {phoneTypes.map((phoneType) => {
                  const id = phoneType.id ?? phoneType.phoneTypeId ?? phoneType.PhoneTypeId;
                  const name = phoneType.description ?? phoneType.name ?? phoneType.Name;
                  return (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="col-2">
              <button type="button" className="btn btn-primary w-100" onClick={handleAddPhone}>
                {t("COMMON.SAVE")}
              </button>
            </div>
          </div>

          {isLoadingPhoneModal ? (
            <Spinner />
          ) : (
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>{t("CUSTOMER.CONTACTS_LIST.COLUMN_NUMBER")}</th>
                  <th>{t("CUSTOMER.CONTACTS_LIST.EXTENSION")}</th>
                  <th>{t("COMMON.TYPE")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {currentPhones.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-muted">
                      {t("CUSTOMER.CONTACTS_LIST.NO_PHONES")}
                    </td>
                  </tr>
                ) : (
                  currentPhones.map((phone) => (
                    <tr key={phone.id}>
                      <td>{phone.number}</td>
                      <td>{phone.extension}</td>
                      <td>{phoneTypeName(phone.phoneTypeId)}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeletePhone(phone.id)}
                        >
                          {t("CUSTOMER.CONTACTS_LIST.REMOVE")}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closePhoneModal}>
            {t("COMMON.CLOSE")}
          </Button>
        </Modal.Footer>
      </Modal>

      <DeleteModal
        show={Boolean(contactToDelete)}
        handleClose={() => setContactToDelete(null)}
        itemName={contactToDelete?.name}
        itemRemoveId={contactToDelete?.contactId}
        callBackRemove={confirmDeleteContact}
      />

      <Modal show={Boolean(emailModalContact)} onHide={closeEmailModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("CUSTOMER.CONTACTS_LIST.ADD_EMAILS_TITLE")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-2 align-items-end mb-3">
            <div className="col-8">
              <label className="form-label">{t("CUSTOMER.CONTACTS_LIST.EMAIL_LABEL")}</label>
              <input
                type="text"
                className="form-control"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
              />
            </div>
            <div className="col-4">
              <button type="button" className="btn btn-primary w-100" onClick={handleAddEmail}>
                {t("COMMON.SAVE")}
              </button>
            </div>
          </div>

          {isLoadingEmailModal ? (
            <Spinner />
          ) : (
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>{t("CUSTOMER.CONTACTS_LIST.EMAIL_LABEL")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {currentEmails.length === 0 ? (
                  <tr>
                    <td colSpan={2} className="text-muted">
                      {t("CUSTOMER.CONTACTS_LIST.NO_EMAILS")}
                    </td>
                  </tr>
                ) : (
                  currentEmails.map((email) => (
                    <tr key={email.id}>
                      <td>{email.email}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteEmail(email.id)}
                        >
                          {t("CUSTOMER.CONTACTS_LIST.REMOVE")}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEmailModal}>
            {t("COMMON.CLOSE")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
